import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Button, Card, Chip, FormField, Modal, Select, toast } from '@/ui';
import { maskPhone, revealOrMask } from '@/lib/admin-mask';
import { formatMoney, OFFER_PRICE_FRACTION_DIGITS, TOTAL_FRACTION_DIGITS } from '@/lib/money';
import { pairOf } from '@/lib/task-currency';
import {
  RESOLUTION_NOTE_MAX,
  TASK_RESOLUTIONS,
  WARNED_QUEUE_SORT_DEFAULT,
  fetchWarnedTaskQueue,
  resolveWarnedTask,
  type TaskResolution,
  type WarnedQueueSort,
  type WarnedTaskQueueRow,
} from './api';
import { adminKeys } from './query-keys';
import { invalidateAfterTaskResolve } from './invalidation';
import { RESOLUTION_HINT_KEYS, RESOLUTION_LABEL_KEYS } from './labels';
import { adminErrorMessage } from './error-message';
import { formatDateTime } from './format';
import { ListStatus, LoadMore } from './ListStatus';
import { Textarea } from './Textarea';
import { flattenPages, usePagedQuery } from './use-paged-query';
import styles from './Admin.module.css';

/**
 * UYARI ALAN ISLEMLER — COZUM KUYRUGU (`/yonetim/uyarilar`, 2026-09-23 karar Q3).
 *
 * Musteri "odeme yapilmadi" uyarisi gonderdiginde (ya da supurucu onaylanmamis odemeyi
 * uyariya cektiginde) islem 'warning'de KALIR: burodan "Odeme alindi" artik gelemez
 * (completed <- yalniz pending). Kapatmanin TEK yolu burasi: yonetici iki tarafla gorusur ve
 *   'resolved'  -> doviz ulasti / anlasmazlik giderildi
 *   'refunded'  -> musterinin odemesi iade ettirildi
 * yazar. Kayit geri alinamaz, iki tarafa bildirim gider; bu yuzden secim + not + onay tek bir
 * pencerede ve onay dugmesi secim yapilmadan acilmaz.
 *
 * Liste deseni diger platform listeleriyle ayni: sonsuz sayfa, `snapshotAt` ilk sayfadan
 * (use-paged-query.ts), telefonlar VARSAYILAN MASKELI (PlatformUsersPage ile ayni kural).
 */
export function PlatformWarnedTasksPage() {
  const { t } = useTranslation();
  const [sort, setSort] = useState<WarnedQueueSort>(WARNED_QUEUE_SORT_DEFAULT);
  const [revealContact, setRevealContact] = useState(false);
  const [resolving, setResolving] = useState<WarnedTaskQueueRow | null>(null);

  const query = usePagedQuery(adminKeys.warnedTasks(sort), (pageParam) =>
    fetchWarnedTaskQueue({ ...pageParam, sort }),
  );
  const rows = flattenPages(query.data);

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{t('admin.warned.title')}</h1>
            <p className={styles.subtitle}>{t('admin.warned.subtitle')}</p>
            {/* `total` YOK — gosterilen sayi YUKLENEN satirlarin sayisi. */}
            <p className={styles.muted}>{t('admin.warned.loadedCount', { n: rows.length })}</p>
          </div>
        </div>
        <div className={styles.filterRow}>
          <FormField label={t('filter.sort')} className={styles.filterField}>
            <Select
              value={sort}
              options={[
                { value: 'warnedAt:asc', label: t('admin.warned.sortOldest') },
                { value: 'warnedAt:desc', label: t('admin.warned.sortNewest') },
              ]}
              onChange={(event) => {
                const value = event.target.value;
                if (value === 'warnedAt:asc' || value === 'warnedAt:desc') setSort(value);
              }}
            />
          </FormField>
        </div>
        <label className={styles.revealToggle}>
          <input
            type="checkbox"
            checked={revealContact}
            onChange={(event) => {
              setRevealContact(event.target.checked);
            }}
          />
          {t('admin.revealContact')}
        </label>
      </Card>

      <ListStatus
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={query.isSuccess && rows.length === 0}
        emptyIcon={<ShieldCheck size={28} aria-hidden="true" />}
        emptyTitle={t('admin.warned.empty')}
        onRetry={() => {
          void query.refetch();
        }}
      />

      {rows.length > 0 ? (
        <Card padding="lg">
          <div className={styles.rowList}>
            {rows.map((row) => (
              <WarnedTaskRow
                key={row.taskId}
                row={row}
                revealContact={revealContact}
                onResolve={() => {
                  setResolving(row);
                }}
              />
            ))}
          </div>
          <LoadMore
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => {
              void query.fetchNextPage();
            }}
          />
        </Card>
      ) : null}

      {resolving === null ? null : (
        <ResolveDialog
          row={resolving}
          onClose={() => {
            setResolving(null);
          }}
        />
      )}
    </div>
  );
}

function WarnedTaskRow({
  row,
  revealContact,
  onResolve,
}: {
  row: WarnedTaskQueueRow;
  revealContact: boolean;
  onResolve: () => void;
}) {
  const { t } = useTranslation();
  // Birimler sunucu sozlesmesiyle (karar Q13): miktar islem biriminde, tutar karsi birimde.
  const amountCurrency = pairOf(row.taskTypeCode).currency;
  const counterCurrency = row.counterCurrency || pairOf(row.taskTypeCode).secondCurrency;
  const phone = (value: string): string =>
    value === '' ? '—' : revealOrMask(value, revealContact, maskPhone);

  return (
    <div className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.rowName}>
          {t('admin.warned.task', { id: row.taskId })} · {row.taskTypeCode}{' '}
          {t(row.taskIsBuying ? 'labels.buy' : 'labels.sell')}
        </span>
        <span className={styles.muted}>
          {t('admin.warned.warnedAt', { date: formatDateTime(row.warnedAt) })}
          {row.decidedAt === null
            ? ''
            : ` · ${t('admin.warned.decidedAt', { date: formatDateTime(row.decidedAt) })}`}
        </span>
        <span className={styles.muted}>
          {t('admin.warned.amount')}: {formatMoney(row.taskAmount, TOTAL_FRACTION_DIGITS)}{' '}
          {amountCurrency}
          {row.offerPrice === null ? (
            <> · {t('admin.warned.noOffer')}</>
          ) : (
            <>
              {' · '}
              {t('admin.warned.price')}: {formatMoney(row.offerPrice, OFFER_PRICE_FRACTION_DIGITS)}{' '}
              {counterCurrency}
              {' · '}
              {t('admin.warned.total')}:{' '}
              {formatMoney(row.taskAmount * row.offerPrice, TOTAL_FRACTION_DIGITS)} {counterCurrency}
            </>
          )}
        </span>
        <span className={styles.muted}>
          {t('admin.warned.customer')}: {row.customerFullName || t('admin.users.unnamed')} ·{' '}
          {phone(row.customerPhone)}
        </span>
        <span className={styles.muted}>
          {t('admin.warned.exchange')}: {row.exchangeFullName || t('admin.users.unnamed')}
          {row.exchangeOrganizationName === null ? '' : ` (${row.exchangeOrganizationName})`} ·{' '}
          {phone(row.exchangePhone)}
        </span>
      </div>
      <div className={styles.badges}>
        <Chip tone="warning">{t('report.statusWarning')}</Chip>
        <Button size="sm" onClick={onResolve}>
          {t('admin.warned.resolveAction')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Sonuc secimi + not + onay. Onay dugmesi secim yapilmadan PASIF: varsayilan bir sonuc
 * (orn. 'resolved') secili gelseydi yanlislikla kaydedilebilirdi ve kayit geri alinamiyor.
 */
function ResolveDialog({ row, onClose }: { row: WarnedTaskQueueRow; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [resolution, setResolution] = useState<TaskResolution | null>(null);
  const [note, setNote] = useState('');
  const noteOver = note.trim().length > RESOLUTION_NOTE_MAX;

  const mutation = useMutation({
    mutationFn: resolveWarnedTask,
    onSuccess: async (_data, variables) => {
      toast({
        message: t('admin.warned.resolvedDone', {
          resolution: t(RESOLUTION_LABEL_KEYS[variables.resolution] ?? variables.resolution),
        }),
        tone: 'success',
      });
      onClose();
      await invalidateAfterTaskResolve(queryClient);
    },
    onError: async () => {
      // 409 TASK_NOT_IN_WARNING: baska bir yonetici once kapatmis. Liste tazelenir ki satir
      // ekranda "hala uyarida" gibi kalmasin; hata metni pencerede gosterilir.
      await invalidateAfterTaskResolve(queryClient);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.warned.resolveTitle', { id: row.taskId })}
      width="sm"
      dismissOnScrim={!mutation.isPending}
      footer={
        <>
          <Button tone="ghost" onClick={onClose} disabled={mutation.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={resolution === null || noteOver}
            loading={mutation.isPending}
            onClick={() => {
              if (resolution === null || noteOver) return;
              mutation.mutate({ taskId: row.taskId, resolution, note });
            }}
          >
            {t('admin.warned.confirmAction')}
          </Button>
        </>
      }
    >
      <div className={styles.composer}>
        <fieldset className={styles.choiceGroup}>
          <legend>{t('admin.warned.resolutionLabel')}</legend>
          {TASK_RESOLUTIONS.map((value) => (
            <label key={value} className={styles.revealToggle}>
              <input
                type="radio"
                name="resolution"
                value={value}
                checked={resolution === value}
                onChange={() => {
                  setResolution(value);
                }}
              />
              {t(RESOLUTION_LABEL_KEYS[value] ?? value)} — {t(RESOLUTION_HINT_KEYS[value] ?? value)}
            </label>
          ))}
        </fieldset>

        <FormField
          label={t('admin.warned.noteLabel')}
          hint={t('admin.warned.noteHint')}
          {...(noteOver ? { error: t('admin.errors.resolutionNoteTooLong') } : {})}
        >
          <Textarea
            value={note}
            maxLength={RESOLUTION_NOTE_MAX + 50}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </FormField>
        <span className={[styles.counter, noteOver ? styles.counterOver : ''].join(' ')}>
          {note.trim().length} / {RESOLUTION_NOTE_MAX}
        </span>

        <p className={styles.warningNotice}>{t('admin.warned.irreversible')}</p>

        {mutation.isError ? (
          <p className={styles.error} role="alert">
            {adminErrorMessage(mutation.error, t)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
